// Pure translation layer between the wizard's `form` state and the Convex
// mutation args. Splits one fat form into:
//   - createArgs       → createCampaign({ name })
//   - settingsArgs     → updateCampaignSettings(campaignId, ...rest)
//   - dummyArgs[]      -> legacy preview seed entries only
//
// Most default-shaped fields are omitted from settingsArgs, but campaign
// homebrew is sent even when all values are false. That makes "no survival
// settings" a durable campaign choice instead of an indistinguishable
// legacy/unset state.

import {
  DAYS,
  FEATURES,
  ALL_HOMEBREW_KEYS,
  TEST_CAMPAIGN_CHARACTERS,
  DEFAULT_TEST_PRESET_CATEGORIES,
  TEST_PRESET_CATEGORY_OPTIONS,
  TEST_PRESET_CATEGORY_FEATURE_GATES,
} from './constants';
import { getEffectiveCampaignFeatures } from '../../../../../campaignFeatureAccess';
import { TIME_OPTIONS } from './timeOptions';

// ---- key renames ------------------------------------------------------

// Wizard uses short keys for the per-element link mode picker; the F.2
// `linkedElements` schema uses the longer "library / rules" suffixes that
// match the existing legacy field names.
export const LINK_MODE_KEY_MAP = {
  npc: 'npcLibrary',
  items: 'itemLibrary',
  crafting: 'craftingRules',
  professions: 'professionRules',
  shops: 'shops',
  systems: 'systems',
};
const LINK_MODE_VALUES = new Set(['off', 'copy', 'link']);

// ---- helpers ----------------------------------------------------------

// Build a free-text schedule string the dashboard's countdownLabel parser
// recognises (it just needs the leading day-of-week token to match). Empty
// daySchedule and `scheduleTBD=true` both return '' so the server clears
// the legacy `schedule` field. Week-order, never insertion-order.
export const derivedScheduleString = (daySchedule, scheduleTBD) => {
  if (scheduleTBD) return '';
  const map = daySchedule || {};
  if (Object.keys(map).length === 0) return '';
  return DAYS
    .filter((d) => map[d.key] != null && map[d.key] !== '')
    .map((d) => {
      const time = map[d.key];
      const opt = TIME_OPTIONS.find((o) => o.value === time);
      return `${d.full}s · ${opt ? opt.label : time}`;
    })
    .join(' · ');
};

export const mapLinkModes = (linkModes) => {
  const out = {};
  for (const [shortKey, value] of Object.entries(linkModes || {})) {
    const schemaKey = LINK_MODE_KEY_MAP[shortKey];
    if (!schemaKey) {
      throw new Error(`Unknown linkModes key: ${shortKey}`);
    }
    if (value === undefined) continue;
    if (!LINK_MODE_VALUES.has(value)) {
      throw new Error(`linkModes.${shortKey} must be one of off, copy, link`);
    }
    out[schemaKey] = value;
  }
  return out;
};

export const mapEnabledFeatures = (features) => {
  const out = {};
  for (const [key, value] of Object.entries(features || {})) {
    if (key === 'auditLog' || key === 'auditlog') continue;
    out[key] = !!value;
  }
  return out;
};

const getTestCharacterPresetCategories = (form) => ({
  ...DEFAULT_TEST_PRESET_CATEGORIES,
  ...(form?.testPresetCategories || {}),
  ...(form?.testCharacterPresetCategories || {}),
});

const applyFeatureGatesToTestCategories = (categories, features = {}) => {
  const effectiveFeatures = getEffectiveCampaignFeatures(features);
  return Object.fromEntries(
    TEST_PRESET_CATEGORY_OPTIONS.map((option) => {
      const requiredFeatures = TEST_PRESET_CATEGORY_FEATURE_GATES[option.key] || [];
      const featureEnabled = requiredFeatures.every((featureKey) => effectiveFeatures[featureKey] !== false);
      return [option.key, featureEnabled && categories[option.key] !== false];
    }),
  );
};

// Wizard collects a count plus group-level preset toggles for local sandbox
// preview. Keep the legacy payload shape for compatibility callers.
export const buildDummyCharacters = (dummyCount, form = {}) => {
  const n = Math.max(0, Math.min(TEST_CAMPAIGN_CHARACTERS.length, Math.floor(Number(dummyCount) || 0)));
  const categories = applyFeatureGatesToTestCategories(
    getTestCharacterPresetCategories(form),
    form.features || {},
  );
  return TEST_CAMPAIGN_CHARACTERS.slice(0, n).map((character) => {
    return {
      name: character.name,
      race: character.race,
      charClass: character.charClass,
      maxHp: character.maxHp,
      testPresetKey: character.presetKey,
      testPresetCategories: { ...categories },
    };
  });
};

// ---- defaults predicates ---------------------------------------------

const allFeaturesEnabled = (features) =>
  FEATURES.every((f) => !!(features && features[f.key]));
// ---- main adapter -----------------------------------------------------

export const buildWorldPlan = (form) => {
  if (!form || form.origin !== 'scratch') return { mode: 'none' };
  if (form.worldMode === 'existing' && form.worldId) {
    return { mode: 'existing', worldId: form.worldId };
  }
  if (form.worldMode === 'new' && form.newWorldName && form.newWorldName.trim()) {
    return { mode: 'new', name: form.newWorldName.trim() };
  }
  return { mode: 'isolated' };
};

export const forgePayloadToMutationArgs = (form) => {
  if (!form || typeof form.name !== 'string' || form.name.trim().length === 0) {
    throw new Error('Campaign name is required');
  }

  const name = form.name.trim();
  const createArgs = { name };
  const settingsArgs = {};

  // Identity
  if (form.tagline && form.tagline.trim()) {
    settingsArgs.tagline = form.tagline.trim();
  }
  if (form.baseTheme) settingsArgs.baseThemeId = form.baseTheme;
  if (form.baseVariant) settingsArgs.baseVariant = form.baseVariant;

  // Schedule — send both the derived legacy string AND the structured
  // daySchedule object so the dashboard's countdownLabel parser and any
  // future schedule-aware UI can read whichever shape they prefer.
  const schedule = derivedScheduleString(form.daySchedule, form.scheduleTBD);
  if (schedule) {
    settingsArgs.schedule = schedule;
    settingsArgs.daySchedule = form.daySchedule;
  }

  // Features — omit when every feature is enabled (the wizard's default).
  if (!allFeaturesEnabled(form.features)) {
    settingsArgs.enabledFeatures = mapEnabledFeatures(form.features);
  }

  // Homebrew — preserve explicit false values so the campaign can carry the
  // user's "no survival settings" intent into later dashboard/App gating.
  if (form.homebrew && typeof form.homebrew === 'object') {
    settingsArgs.homebrew = Object.fromEntries(
      ALL_HOMEBREW_KEYS.map((key) => [key, !!form.homebrew[key]]),
    );
  }

  // Origin-specific intent fields. Existing-campaign imports are server
  // orchestrated; Forge records provenance but does not send per-element
  // link/copy modes from the client.
  if (form.origin === 'template' && form.templateId) {
    settingsArgs.templateOrigin = form.templateId;
  }
  let sourceImportPlan = null;
  if (form.origin === 'existing' && form.existingId) {
    settingsArgs.originSourceCampaignId = form.existingId;
    // Legacy compatibility: old readers still use sourceCampaignId for the
    // duplicate-from-existing source. Rules-copy uses rulesSourceCampaignId
    // below so the two concepts no longer overwrite each other.
    settingsArgs.sourceCampaignId = form.existingId;
    sourceImportPlan = { sourceCampaignId: form.existingId };
  }

  // Real world selection is handled by worlds.applyCampaignWorldSelection.
  // Keep it out of generic campaign settings so `worldEntityId` is only
  // written after server-side world membership validation.
  const worldPlan = buildWorldPlan(form);

  // Rules mode + copy-source override. Keep this separate from the existing
  // campaign origin source so "create from A, copy rules from B" is durable.
  if (form.rulesMode) settingsArgs.rulesMode = form.rulesMode;
  if (form.rulesMode === 'copy' && form.copyFromId) {
    settingsArgs.rulesSourceCampaignId = form.copyFromId;
  }

  // Email invites — collected here as intent; F.6 doesn't send anything,
  // a later phase delivers them. invitedCollaborators is dropped for now
  // since there's no backend column today.
  if (form.invitedEmails && form.invitedEmails.length > 0) {
    settingsArgs.pendingEmailInvites = [...form.invitedEmails];
  }

  const dummyArgs = buildDummyCharacters(form.dummyCount, form);

  return { createArgs, settingsArgs, dummyArgs, worldPlan, sourceImportPlan };
};
